import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Article from './Article';
import './ArticleList.css';
import { debugData } from '../debug/debugData';

const ArticleListDebug = () => {
  const { jsonName } = useParams();
  const [articles, setArticles] = useState([]);
  const [pickedArticles, setPickedArticles] = useState(() => {
    return JSON.parse(localStorage.getItem('pickedArticles')) || {};
  });
  const [globalAvailableStock, setGlobalAvailableStock] = useState({}); // Global stock per locationId
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [hasProcessed, setHasProcessed] = useState(false); // Track if Process All has been used
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load debug data from the source file
    if (jsonName && debugData[jsonName]) {
      const data = debugData[jsonName];
      if (data.meta.status === "error") {
        setArticles([]);
        setError('Debug data contains an error status');
      } else {
        setArticles(data.data.items);

        // Initialize global available stock for all locations across all articles
        const stockMap = {};
        data.data.items.forEach(article => {
          // Add main location - use paperid + locationId as key
          if (article.locationId && article.available !== undefined && article.paperid) {
            const key = `${article.paperid}_${article.locationId}`;
            stockMap[key] = article.available;
          }
          // Add alternative locations
          if (article.locations && article.paperid) {
            article.locations.forEach(location => {
              if (location.locationId && location.available !== undefined) {
                // Use paperid + locationId as key
                const key = `${article.paperid}_${location.locationId}`;
                // Use the maximum available if location appears multiple times
                stockMap[key] = Math.max(
                  stockMap[key] || 0,
                  location.available
                );
              }
            });
          }
        });
        setGlobalAvailableStock(stockMap);
      }
    } else {
      setError(`Debug JSON '${jsonName}' not found. Available: ${Object.keys(debugData).join(', ')}`);
    }
  }, [jsonName]);

  const handleProcess = async () => {
    // Early return if already processing or has been processed
    if (isProcessing || hasProcessed) {
      alert('Deze actie is al uitgevoerd of wordt verwerkt. Wacht tot de verwerking is voltooid.');
      return;
    }

    // Check if all articles are fully picked (each order line separately)
    const notFullyPicked = articles.filter(article => {
      const key = `${article.id}_${article.orderNumber}`;
      const pickedQty = pickedArticles[key]?.mainQuantity || 0;
      const altLocQty = Object.values(pickedArticles[key]?.locations || {}).reduce((sum, qty) => sum + qty, 0);
      const totalPicked = pickedQty + altLocQty;
      return totalPicked < article.qty;
    });

    // If not everything is picked, show confirmation modal
    if (notFullyPicked.length > 0) {
      setShowConfirmModal(true);
      return; // Wait for user confirmation
    }

    // If everything is picked, proceed directly
    proceedWithProcessing();
  };

  const proceedWithProcessing = async () => {
    setShowConfirmModal(false);
    setIsProcessing(true);
    setHasProcessed(true); // Mark as processed to prevent duplicate submissions

    const items = Object.entries(pickedArticles).map(([key, { mainQuantity, locations }]) => {
      // Parse key: "id_orderNumber"
      const [id, orderNumber] = key.split('_');
      const article = articles.find(article => article.id === parseInt(id) && article.orderNumber === orderNumber);
      if (!article) {
        console.error(`Article with key ${key} not found`);
        return null;
      }

      return {
        orderNumber: article.orderNumber,
        id: parseInt(id),
        job: article.job,
        locations: [
          {
            pickQuantity: mainQuantity,
            locationId: article.locationId
          },
          ...Object.entries(locations || {}).map(([locationId, pickQuantity]) => ({
            pickQuantity,
            locationId: parseInt(locationId)
          }))
        ].filter(location => location.pickQuantity > 0)
      };
    }).filter(item => item !== null);

    if (items.length === 0) {
      setError('Er is nog niets gepakt...');
      setIsProcessing(false);
      return;
    }

    const payload = {
      items,
      picklist: {
        key: 'DEBUG-' + jsonName,
        code: ""
      }
    };

    console.log('[DEBUG MODE] Payload that would be sent:', JSON.stringify(payload, null, 2));

    // In debug mode, show the JSON in a popup
    const jsonString = JSON.stringify(payload, null, 2);

    // Create a modal-like popup to display JSON
    const popupContent = `
      <div style="font-family: monospace; white-space: pre-wrap; max-height: 70vh; overflow: auto; text-align: left;">
${jsonString}
      </div>
    `;

    // Create a temporary div for the popup
    const popup = document.createElement('div');
    popup.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 9999;">
        <div style="background: white; padding: 20px; border-radius: 8px; max-width: 80%; max-height: 80%; overflow: auto; position: relative;">
          <h3 style="margin-top: 0; color: #333;">🐛 DEBUG MODE - Payload JSON</h3>
          ${popupContent}
          <button id="closePopup" style="margin-top: 15px; padding: 10px 20px; background: #2196F3; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">Close</button>
        </div>
      </div>
    `;
    document.body.appendChild(popup);

    // Close popup when button is clicked
    document.getElementById('closePopup').onclick = () => {
      document.body.removeChild(popup);
    };

    localStorage.removeItem('pickedArticles');
    setPickedArticles({});
    setIsProcessing(false);
  };

  return (
    <div className="article-list">
      <div style={{
        backgroundColor: '#ffeb3b',
        padding: '10px',
        marginBottom: '10px',
        borderRadius: '5px',
        fontWeight: 'bold',
        textAlign: 'center'
      }}>
        🐛 DEBUG MODE: {jsonName}
      </div>

      {error && (
        <>
          <p className="error-message">{error}</p>
          <button onClick={() => navigate('/')} className="btn-back">
            ← Terug
          </button>
        </>
      )}

      {articles.length === 0 && !error && (
        <>
          <p>Loading debug data...</p>
          <button onClick={() => navigate('/')} className="btn-back">
            ← Terug
          </button>
        </>
      )}

      {articles.map((article, index) => (
        <Article
          key={`${article.id}-${index}`}
          article={article}
          pickedArticles={pickedArticles}
          setPickedArticles={setPickedArticles}
          conneoId="DEBUG"
          batchId={jsonName}
          globalAvailableStock={globalAvailableStock}
          setGlobalAvailableStock={setGlobalAvailableStock}
        />
      ))}

      {articles.length > 0 && (
        <div className="button-group-single">
          <button
            onClick={handleProcess}
            className="btn-process"
            disabled={isProcessing || hasProcessed || Object.keys(pickedArticles).length === 0}
          >
            {hasProcessed ? 'Verwerkt ✓' : isProcessing ? 'Bezig...' : 'Verstuur (Debug - No POST)'}
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '400px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Let op!</h3>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
              Nog niet alles is gepakt.
              <br /><br />
              Weet je zeker dat je wilt doorgaan met verwerken?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Annuleren
              </button>
              <button
                onClick={proceedWithProcessing}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  backgroundColor: '#28a745',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Doorgaan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleListDebug;
